import React, { useState } from 'react';
import { Icon } from '../components/common/Icons';

export default function SettingsPage() {
  // State hooks
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [defaultView, setDefaultView] = useState(() => localStorage.getItem('view_mode') || 'grid');
  const [noteDensity, setNoteDensity] = useState(() => localStorage.getItem('note_density') || 'comfortable');
  const [confirmDelete, setConfirmDelete] = useState(() => localStorage.getItem('confirm_delete') !== 'false');

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // No toast on toggling view mode or density
  const handleViewChange = (newView) => {
    setDefaultView(newView);
    localStorage.setItem('view_mode', newView);
  };

  const handleDensityChange = (newDensity) => {
    setNoteDensity(newDensity);
    localStorage.setItem('note_density', newDensity);
  };

  const handleConfirmDeleteToggle = () => {
    const nextVal = !confirmDelete;
    setConfirmDelete(nextVal);
    localStorage.setItem('confirm_delete', String(nextVal));
  };

  return (
    <div className="settings-page-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <Icon name="settings" size={24} />
            <span>Settings</span>
          </h1>
          <p className="page-subtitle">
            Configure your workspace appearance, note defaults, and preferences.
          </p>
        </div>
      </div>

      <div className="settings-grid-layout">
        {/* Main Column */}
        <div className="settings-main-col">
          {/* Appearance Section */}
          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon">
                <Icon name="sun" size={18} />
              </div>
              <div>
                <h2 className="settings-card-title">Theme</h2>
                <p className="settings-card-subtitle">Choose the visual aesthetic for your workspace</p>
              </div>
            </div>

            <div className="settings-option-grid">
              {/* Dark Obsidian */}
              <button
                type="button"
                className={`theme-choice-card ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => handleThemeChange('dark')}
              >
                <div className="theme-preview dark-preview">
                  <div className="preview-topbar" />
                  <div className="preview-body">
                    <div className="preview-card" />
                    <div className="preview-card" />
                  </div>
                </div>
                <div className="theme-choice-footer">
                  <div className="theme-choice-label">
                    <Icon name="moon" size={15} />
                    <span>Dark Obsidian</span>
                  </div>
                  {theme === 'dark' ? (
                    <span className="theme-active-check">
                      <Icon name="check" size={12} />
                    </span>
                  ) : (
                    <span className="theme-radio-dot" />
                  )}
                </div>
              </button>

              {/* Light Slate */}
              <button
                type="button"
                className={`theme-choice-card ${theme === 'light' ? 'active' : ''}`}
                onClick={() => handleThemeChange('light')}
              >
                <div className="theme-preview light-preview">
                  <div className="preview-topbar" />
                  <div className="preview-body">
                    <div className="preview-card" />
                    <div className="preview-card" />
                  </div>
                </div>
                <div className="theme-choice-footer">
                  <div className="theme-choice-label">
                    <Icon name="sun" size={15} />
                    <span>Light Slate</span>
                  </div>
                  {theme === 'light' ? (
                    <span className="theme-active-check">
                      <Icon name="check" size={12} />
                    </span>
                  ) : (
                    <span className="theme-radio-dot" />
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Notes Layout Section */}
          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon">
                <Icon name="grid" size={18} />
              </div>
              <div>
                <h2 className="settings-card-title">Notes Layout</h2>
                <p className="settings-card-subtitle">Control default presentation across your library</p>
              </div>
            </div>

            <div className="settings-field-group">
              <label className="settings-field-label">Default View Format</label>
              <div className="settings-slider-toggle">
                <div className={`settings-slider-pill ${defaultView === 'grid' ? 'left' : 'right'}`} />
                <button
                  type="button"
                  className={`settings-slider-btn ${defaultView === 'grid' ? 'active' : ''}`}
                  onClick={() => handleViewChange('grid')}
                >
                  <Icon name="grid" size={15} />
                  <span>Grid View</span>
                </button>
                <button
                  type="button"
                  className={`settings-slider-btn ${defaultView === 'list' ? 'active' : ''}`}
                  onClick={() => handleViewChange('list')}
                >
                  <Icon name="list" size={15} />
                  <span>List View</span>
                </button>
              </div>
            </div>

            <div className="settings-divider" />

            <div className="settings-field-group">
              <label className="settings-field-label">Preview Style</label>
              <div className="settings-slider-toggle">
                <div className={`settings-slider-pill ${noteDensity === 'comfortable' ? 'left' : 'right'}`} />
                <button
                  type="button"
                  className={`settings-slider-btn ${noteDensity === 'comfortable' ? 'active' : ''}`}
                  onClick={() => handleDensityChange('comfortable')}
                >
                  <Icon name="note" size={15} />
                  <span>Comfortable</span>
                </button>
                <button
                  type="button"
                  className={`settings-slider-btn ${noteDensity === 'compact' ? 'active' : ''}`}
                  onClick={() => handleDensityChange('compact')}
                >
                  <Icon name="sparkles" size={15} />
                  <span>Compact</span>
                </button>
              </div>
            </div>

            <div className="settings-divider" />

            <div className="settings-toggle-row">
              <div className="toggle-text-col">
                <span className="toggle-title">Confirm Before Deleting Notes</span>
                <span className="toggle-desc">Show confirmation modal before moving notes to Trash</span>
              </div>
              <button
                type="button"
                className={`modern-switch ${confirmDelete ? 'on' : ''}`}
                onClick={handleConfirmDeleteToggle}
                role="switch"
                aria-checked={confirmDelete}
                aria-label="Confirm before deleting notes"
              >
                <span className="switch-slider" />
              </button>
            </div>
          </div>

          {/* Keyboard Shortcuts Cheatsheet */}
          <div className="settings-card">
            <div className="settings-card-header">
              <div className="settings-card-icon">
                <Icon name="sparkles" size={18} />
              </div>
              <div>
                <h2 className="settings-card-title">Keyboard Shortcuts</h2>
                <p className="settings-card-subtitle">Quick hotkeys for fast navigation</p>
              </div>
            </div>

            <div className="shortcuts-table">
              <div className="shortcut-row">
                <span className="shortcut-desc">Search</span>
                <div className="shortcut-keys">
                  <kbd className="settings-kbd">⌘K</kbd>
                  <span className="shortcut-or">or</span>
                  <kbd className="settings-kbd">Ctrl+K</kbd>
                </div>
              </div>
              <div className="shortcut-row">
                <span className="shortcut-desc">Create New Note or Category</span>
                <div className="shortcut-keys">
                  <kbd className="settings-kbd">⌘N</kbd>
                  <span className="shortcut-or">or</span>
                  <kbd className="settings-kbd">Ctrl+N</kbd>
                </div>
              </div>
              <div className="shortcut-row">
                <span className="shortcut-desc">Close Search</span>
                <div className="shortcut-keys">
                  <kbd className="settings-kbd">Esc</kbd>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
